import { useEffect, useRef } from "react";
import { broadcastApi } from "@/services/broadcastApi";
import { useBannerAlert } from "@/contexts/BannerAlertContext";

const POLL_INTERVAL_MS = 60_000;
const BROADCAST_BANNER_ID = "__broadcast__";
const DISMISSED_KEY = "broadcast_dismissed_at";

/**
 * Polls the backend every 60 s and syncs the active broadcast banner into the
 * BannerAlertContext. If the user dismisses the banner it stays dismissed for
 * the current session, but a new/updated banner (different updated_at) will
 * reappear.
 */
export function useBroadcastBanner() {
  const { addBanner, removeBanner, banners } = useBannerAlert();
  const lastUpdatedAt = useRef<string | null>(null);

  const sync = async () => {
    try {
      const banner = await broadcastApi.get();

      if (!banner || !banner.is_active) {
        // Backend cleared the banner — remove it locally too
        removeBanner(BROADCAST_BANNER_ID);
        lastUpdatedAt.current = null;
        return;
      }

      // Check if user dismissed this exact version
      const dismissedAt = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissedAt === banner.updated_at) return;

      // Already showing this exact version
      if (lastUpdatedAt.current === banner.updated_at) return;

      // New or updated banner — remove old, add fresh
      removeBanner(BROADCAST_BANNER_ID);
      lastUpdatedAt.current = banner.updated_at;
      addBanner({
        id: BROADCAST_BANNER_ID,
        variant: banner.variant,
        title: banner.title,
        body: banner.body ?? undefined,
        inline: banner.inline,
      });
    } catch {
      // Silently ignore poll errors (network, auth)
    }
  };

  useEffect(() => {
    sync();
    const timer = setInterval(sync, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the broadcast banner is dismissed by the user, record it so it
  // doesn't reappear during this session unless the backend updates it.
  useEffect(() => {
    const isBroadcastVisible = banners.some((b) => b.id === BROADCAST_BANNER_ID);
    if (!isBroadcastVisible && lastUpdatedAt.current) {
      sessionStorage.setItem(DISMISSED_KEY, lastUpdatedAt.current);
    }
  }, [banners]);
}
